// Trails (docs/164 "Ground": `floor_path`, joining the ways in and out; docs/166 C2 the pinewood's verge):
// the forge places no track yet (docs/166 E4), so the painter works one out from the board's exits, the
// same on every client: the cheapest walk over open ground between each exit and the next, and a worn
// band drawn along the line joining the trail squares' centres, its edge ragged (`on_track` in the
// pinewood's `pine_board.py.txt`). It is drawn only on plain floor, so it never claims a rule.
import type { Squares } from './board';
import type { Ctx } from './context';
import { C } from './context';

/** Squares the trail crosses (a set of `y * w + x`). */
export function trailSquares(ctx: Ctx): Set<number> {
  const { sq } = ctx, { w, h } = sq;
  const out = new Set<number>();
  const exits: number[] = [];
  for (let i = 0; i < w * h; i++) if (sq.board.tiles[i]?.exit && sq.sq[i] === 'floor') exits.push(i);
  if (!exits.length) return out;
  // each run of exit squares is one way in or out; it starts from its middle square
  const groups: number[][] = [], seen = new Set<number>();
  for (const e of exits) {
    if (seen.has(e)) continue;
    const g: number[] = [], todo = [e];
    while (todo.length) {
      const c = todo.pop()!;
      if (seen.has(c) || !exits.includes(c)) continue;
      seen.add(c); g.push(c);
      const x = c % w, y = Math.floor(c / w);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) if (sq.on(x + dx, y + dy)) todo.push((y + dy) * w + x + dx);
    }
    g.sort((a, b) => a - b);
    groups.push(g);
  }
  const mids = groups.map((g) => g[Math.floor(g.length / 2)]!);
  // one way alone wanders to the middle of the board; several are joined each to the next
  const ends: Array<[number, number]> = [];
  if (mids.length === 1) {
    const cx = Math.floor(w / 2), cy = Math.floor(h / 2);
    let best = -1, bd = Infinity;
    for (let i = 0; i < w * h; i++) if (sq.sq[i] === 'floor') { const d = Math.abs(i % w - cx) + Math.abs(Math.floor(i / w) - cy); if (d < bd) { bd = d; best = i; } }
    if (best >= 0) ends.push([mids[0]!, best]);
  } else {
    const cx = w / 2, cy = h / 2;
    const order = [...mids].sort((a, b) => Math.atan2(Math.floor(a / w) - cy, a % w - cx) - Math.atan2(Math.floor(b / w) - cy, b % w - cx));
    for (let i = 0; i + 1 < order.length; i++) ends.push([order[i]!, order[i + 1]!]);
  }
  for (const [a, b] of ends) for (const c of walk(ctx, sq, a, b)) out.add(c);
  return out;
}

/** The cheapest 8-way walk from `a` to `b` over open ground (Dijkstra; ties broken by the seed so a trail bends). */
function walk(ctx: Ctx, sq: Squares, a: number, b: number): number[] {
  const { w, h } = sq;
  const cost = (i: number): number => { const s = sq.sq[i]; return s === 'floor' ? 10 + (ctx.roll('trail', i % w, Math.floor(i / w)) % 6) : s === 'rough' ? 40 : s === 'thicket' ? 60 : Infinity; };
  const dist = new Float64Array(w * h).fill(Infinity), prev = new Int32Array(w * h).fill(-1), done = new Uint8Array(w * h);
  dist[a] = 0;
  const open = [a];
  while (open.length) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (dist[open[i]!]! < dist[open[bi]!]!) bi = i;
    const c = open.splice(bi, 1)[0]!;
    if (done[c]) continue;
    done[c] = 1;
    if (c === b) break;
    const x = c % w, y = Math.floor(c / w);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if ((!dx && !dy) || !sq.on(x + dx, y + dy)) continue;
      const n = (y + dy) * w + x + dx;
      // a diagonal step only between two open squares (no cutting a wall's corner)
      if (dx && dy && (cost(y * w + x + dx) === Infinity || cost((y + dy) * w + x) === Infinity)) continue;
      const step = cost(n) * (dx && dy ? 1.42 : 1);
      if (step === Infinity || done[n]) continue;
      if (dist[c]! + step < dist[n]!) { dist[n] = dist[c]! + step; prev[n] = c; open.push(n); }
    }
  }
  if (dist[b] === Infinity) return [];
  const path: number[] = [];
  for (let c = b; c >= 0; c = prev[c]!) path.push(c);
  return path;
}

/** Whether board pixel (px, py) lies on the worn band: three or four pixels either side of the line between neighbouring trail squares' centres, ragged. */
export function onTrail(ctx: Ctx, trail: Set<number>, px: number, py: number): boolean {
  const { w, h } = ctx.sq;
  const cx0 = Math.floor(px / C), cy0 = Math.floor(py / C);
  const at = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < w && y < h && trail.has(y * w + x);
  let best = 99;
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const cx = cx0 + i, cy = cy0 + j;
    if (!at(cx, cy)) continue;
    const ax = cx * C + 12, ay = cy * C + 12;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if ((dx || dy) && !at(cx + dx, cy + dy)) continue;
      if (dx && dy && (at(cx + dx, cy) || at(cx, cy + dy))) continue;   // a diagonal only where no square joins them
      const vx = dx * C, vy = dy * C, ln = vx * vx + vy * vy;
      const u = ln ? Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / ln)) : 0;
      best = Math.min(best, Math.hypot(px - ax - u * vx, py - ay - u * vy));
    }
  }
  return best < 4 + (ctx.roll('rag', px >> 1, py >> 1) % 3);
}
